// ─────────────────────────────────────────────────────────────
// src/pages/TaxCalculator/components/BulkUploader.jsx
// Excel file-upload area, process / download buttons, alerts,
// and results-preview table for bulk mode.
// Pure UI – all state/logic lives in useBulkCalculator hook.
// ─────────────────────────────────────────────────────────────

import React from "react";
import {
  Upload,
  FileSpreadsheet,
  CheckCircle,
  AlertCircle,
  Calculator,
  Download,
} from "lucide-react";

/* ── Results preview table ─────────────────────────────────── */
const ResultsTable = ({ processedData, styles, t, screenSize, currentTheme }) => (
  <div style={styles.card}>
    <h3
      style={{
        fontSize:     screenSize.isMobile ? "18px" : "20px",
        fontWeight:   "700",
        color:        currentTheme.colors.textPrimary,
        marginBottom: "16px",
      }}
    >
      {t.resultsPreview} ({processedData.length} {t.records})
    </h3>

    <div style={styles.tableWrapper}>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>{t.employeeName}</th>
            <th style={styles.th}>{t.insuranceSalary}</th>
            <th style={styles.th}>{t.netSalary}</th>
            <th style={styles.th}>{t.grossSalary}</th>
            <th style={styles.th}>{t.monthlyTax}</th>
            <th style={styles.th}>{t.yearlyTax}</th>
            <th style={styles.th}>{t.martyrsFund}</th>
          </tr>
        </thead>
        <tbody>
          {processedData.slice(0, 10).map((row, index) => (
            <tr key={index}>
              <td style={styles.td}>{row[t.employeeName]}</td>
              <td style={styles.td}>{row[t.insuranceSalary]}</td>
              <td style={styles.td}>{row[t.netSalary]}</td>
              <td style={styles.td}>{row[t.grossSalary]}</td>
              <td style={styles.td}>{row[t.monthlyTax]}</td>
              <td style={styles.td}>{row[t.yearlyTax]}</td>
              <td style={styles.td}>{row[t.martyrsFund]}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {processedData.length > 10 && (
        <div
          style={{
            marginTop: "16px",
            textAlign: "center",
            color:     currentTheme.colors.textMuted,
            fontSize:  screenSize.isMobile ? "12px" : "14px",
          }}
        >
          {t.showingFirst} {processedData.length} {t.recordsDownload}
        </div>
      )}
    </div>
  </div>
);

/* ── Main uploader card ────────────────────────────────────── */
const BulkUploader = ({
  file,
  processing,
  processedData,
  bulkError,
  success,
  onFileUpload,
  onProcess,
  onDownload,
  styles,
  t,
  screenSize,
  currentTheme,
}) => (
  <>
    <div style={styles.card}>
      <h3
        style={{
          fontSize:     screenSize.isMobile ? "18px" : "20px",
          fontWeight:   "700",
          color:        currentTheme.colors.textPrimary,
          marginBottom: "20px",
          display:      "flex",
          alignItems:   "center",
          gap:          "10px",
        }}
      >
        <Upload size={20} />
        {t.uploadExcelFile}
      </h3>

      {/* Drop-zone */}
      <label htmlFor="file-upload">
        <div style={styles.uploadArea}>
          <div style={styles.uploadIcon}>
            <FileSpreadsheet size={screenSize.isMobile ? 40 : 48} />
          </div>
          <div style={styles.uploadText}>{t.clickToUpload}</div>
          <div style={styles.uploadHint}>{t.excelFilesOnly}</div>
        </div>
        <input
          id="file-upload"
          type="file"
          accept=".xlsx,.xls"
          onChange={onFileUpload}
          style={{ display: "none" }}
        />
      </label>

      {file && (
        <div style={styles.fileName}>
          <CheckCircle size={16} />
          {file.name}
        </div>
      )}

      {/* Actions */}
      <div style={styles.buttonGroup}>
        <button
          style={styles.button("primary")}
          onClick={onProcess}
          disabled={!file || processing}
        >
          {processing ? t.processing : t.processExcel}
          <Calculator size={18} />
        </button>

        {processedData && (
          <button style={styles.button("success")} onClick={onDownload}>
            {t.downloadResults}
            <Download size={18} />
          </button>
        )}
      </div>

      {/* Error alert */}
      {bulkError && (
        <div style={styles.alertBox("error")}>
          <AlertCircle size={20} />
          {bulkError}
        </div>
      )}

      {/* Success alert */}
      {success && (
        <div style={styles.alertBox("success")}>
          <CheckCircle size={20} />
          {t.successfullyProcessed} {processedData?.length} {t.employeeRecords}
        </div>
      )}
    </div>

    {/* Preview table */}
    {processedData && processedData.length > 0 && (
      <ResultsTable
        processedData={processedData}
        styles={styles}
        t={t}
        screenSize={screenSize}
        currentTheme={currentTheme}
      />
    )}
  </>
);

export default BulkUploader;